// utils/connectionStatus.js
// ============================================
// Connection availability — "can this tab reach Nexploy right now?"
//
// One small store with one job, deliberately separate from:
//   • authentication (AuthContext / sessionManager — is there a session?)
//   • identity       (hooks/useProfile — who does the server say this is?)
//   • UI errors      (components/session/AppErrorBoundary)
//
// It never ends a session. Being unable to reach the server says nothing
// about whether the session is valid, so the most this store does is show
// the connection screen/banner and keep retrying until the server answers.
//
// Inputs:  apiFetch reports network failures and 502/503/504 answers, the
//          identity probe reports its own outcome, and the browser's
//          online/offline/focus events nudge it.
// Output:  { status: "online" | "offline" | "unavailable", referenceId,
//            checking, nextRetryAt } via useConnectionStatus(), and
//          waitForReachable() — read requests park on it while the server
//          can't be reached and continue the moment it answers again, so
//          every page finishes loading by itself after an outage.
// ============================================
import { useSyncExternalStore } from "react";

// Backoff between automatic recovery attempts; the last value repeats.
export const RETRY_BACKOFF_MS = [2000, 5000, 10000, 20000, 30000];
// A single failed request is not an outage — confirm with one probe before
// anything visible changes (avoids a banner flashing on a one-off blip).
const CONFIRM_DELAY_MS = 1000;
const UNAVAILABLE_HTTP = new Set([502, 503, 504]);
// A parked request also re-tries on its own after this long, as a safety net
// in case no confirmation ever arrives (a failed re-try simply parks again).
const WAITER_FALLBACK_MS = 30000;

const ONLINE = "online";

let state = { status: ONLINE, referenceId: null, checking: false, nextRetryAt: null };
const listeners = new Set();

let probe = null; // async () => ({ ok, invalid, offline, referenceId })
let probing = false;
let attempt = 0;
let timer = null;
let windowListenersInstalled = false;

// Requests parked until the server is reachable again.
const waiters = new Set();

const makeAbortError = () => {
  const err = new Error("The request was cancelled.");
  err.name = "AbortError";
  return err;
};

const settleWaiters = (rejectWith = null) => {
  const pending = Array.from(waiters);
  waiters.clear();
  pending.forEach((waiter) => waiter.settle(rejectWith));
};

const emit = (patch) => {
  const next = { ...state, ...patch };
  if (
    next.status === state.status &&
    next.referenceId === state.referenceId &&
    next.checking === state.checking &&
    next.nextRetryAt === state.nextRetryAt
  ) {
    return;
  }
  state = next;
  listeners.forEach((listener) => listener());
};

const hasSession = () => {
  try {
    return Boolean(localStorage.getItem("token"));
  } catch (err) {
    return false;
  }
};

const isBrowserOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const clearTimer = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

const scheduleProbe = (delayMs) => {
  clearTimer();
  if (!probe || !hasSession()) return;
  emit({ nextRetryAt: Date.now() + delayMs });
  timer = setTimeout(runProbe, delayMs);
};

const nextBackoff = () => {
  const delay = RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];
  attempt += 1;
  return delay;
};

async function runProbe() {
  clearTimer();
  if (!probe || probing) return;
  if (!hasSession()) {
    resetConnection();
    return;
  }

  probing = true;
  emit({ checking: true, nextRetryAt: null });
  let outcome;
  try {
    outcome = (await probe()) || {};
  } catch (err) {
    outcome = {};
  } finally {
    probing = false;
  }

  // The server answered about the session (valid, or explicitly ended — the
  // auth layer handles the latter): either way it is reachable.
  if (outcome.ok || outcome.invalid) {
    attempt = 0;
    emit({ status: ONLINE, referenceId: null, checking: false, nextRetryAt: null });
    // Session over → parked requests are cancelled, not replayed.
    settleWaiters(outcome.invalid ? makeAbortError() : null);
    return;
  }

  emit({
    status: outcome.offline || isBrowserOffline() ? "offline" : "unavailable",
    referenceId: outcome.referenceId || state.referenceId || null,
    checking: false,
  });
  scheduleProbe(nextBackoff());
}

const installWindowListeners = () => {
  if (windowListenersInstalled || typeof window === "undefined") return;
  windowListenersInstalled = true;

  window.addEventListener("online", () => {
    if (state.status !== ONLINE) runProbe();
  });
  window.addEventListener("offline", () => {
    if (!hasSession()) return;
    emit({ status: "offline" });
    attempt = 0;
    scheduleProbe(nextBackoff());
  });
  const retryIfDegraded = () => {
    if (state.status !== ONLINE && !probing) runProbe();
  };
  window.addEventListener("focus", retryIfDegraded);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") retryIfDegraded();
  });
};

// ── Store API ──────────────────────────────────────────────────────────────

export const subscribeConnection = (listener) => {
  installWindowListeners();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getConnectionSnapshot = () => state;

/** Registered once by the identity store: re-asks GET /users/me. */
export const setRecoveryProbe = (fn) => {
  probe = typeof fn === "function" ? fn : null;
};

/** Manual "Try again" — runs a check immediately. */
export const retryConnectionNow = () => {
  attempt = 0;
  return runProbe();
};

/**
 * A request could not reach the server (fetch threw) or got a gateway /
 * service-unavailable answer. While the tab looks healthy this only arms a
 * confirming probe; the banner/screen appears once that probe fails too.
 */
export const reportRequestFailure = ({ offline = false, referenceId = null } = {}) => {
  if (probing || !hasSession()) return;
  installWindowListeners();
  if (offline || isBrowserOffline()) {
    emit({ status: "offline", referenceId });
    if (!timer) scheduleProbe(nextBackoff());
    return;
  }
  if (state.status === ONLINE) {
    if (!timer) scheduleProbe(CONFIRM_DELAY_MS);
    return;
  }
  if (!timer) scheduleProbe(nextBackoff());
};

/**
 * The identity probe itself failed (startup, nothing confirmed yet): the
 * failure is already certain, so show it at once and start the backoff.
 */
export const reportIdentityUnavailable = ({ offline = false, referenceId = null } = {}) => {
  if (!hasSession()) return;
  installWindowListeners();
  emit({
    status: offline || isBrowserOffline() ? "offline" : "unavailable",
    referenceId: referenceId || null,
    checking: false,
  });
  if (!timer && !probing) scheduleProbe(nextBackoff());
};

/** Any request got a real answer back — the server is reachable. */
export const reportRequestSuccess = () => {
  settleWaiters();
  if (probing || state.status === ONLINE) return;
  // Don't just flip to "online": re-check identity first, so a degraded
  // startup (identity never confirmed) can't be marked healthy by some
  // unrelated request while the account is still unverified.
  runProbe();
};

/** Classify a finished apiFetch response. */
export const reportResponse = (response) => {
  if (!response) return;
  if (UNAVAILABLE_HTTP.has(response.status)) reportRequestFailure();
  else reportRequestSuccess();
};

/** Identity confirmed by the server — the tab is healthy. */
export const reportConnectionRestored = () => {
  clearTimer();
  attempt = 0;
  emit({ status: ONLINE, referenceId: null, checking: false, nextRetryAt: null });
  settleWaiters();
};

/**
 * Resolve once the server is known to be reachable again (a recovery probe
 * or any other request got an answer). Rejects with an AbortError when the
 * caller's `signal` aborts or the session ends meanwhile.
 */
export const waitForReachable = (signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(makeAbortError());
      return;
    }
    if (!hasSession()) {
      reject(makeAbortError());
      return;
    }
    let fallbackTimer = null;
    const onAbort = () => waiter.settle(makeAbortError());
    const waiter = {
      settle(error) {
        if (waiter.done) return;
        waiter.done = true;
        waiters.delete(waiter);
        clearTimeout(fallbackTimer);
        signal?.removeEventListener("abort", onAbort);
        if (error) reject(error);
        else resolve();
      },
    };
    waiters.add(waiter);
    signal?.addEventListener("abort", onAbort, { once: true });
    fallbackTimer = setTimeout(() => waiter.settle(null), WAITER_FALLBACK_MS);
    // Make sure something is going to check the server.
    installWindowListeners();
    if (!timer && !probing) scheduleProbe(state.status === ONLINE ? CONFIRM_DELAY_MS : nextBackoff());
  });

/** Session ended (logout) — forget everything. */
export function resetConnection() {
  clearTimer();
  attempt = 0;
  probing = false;
  emit({ status: ONLINE, referenceId: null, checking: false, nextRetryAt: null });
  settleWaiters(makeAbortError());
}

export const useConnectionStatus = () =>
  useSyncExternalStore(subscribeConnection, getConnectionSnapshot, getConnectionSnapshot);
